// components/login-screen.tsx
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { SpinnerGapIcon as Loader2 } from "@phosphor-icons/react/dist/csr/SpinnerGap"
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { signInSchema, type SignInInput } from "@/lib/auth/validation";
import { oauthErrorMessage, signInWithGoogle } from "@/lib/auth-client";

function GoogleLogo() {
  return (
    <svg
      className="mr-2 size-4"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  );
}

function LoginScreenInner() {
  const [submitting, setSubmitting] = useState(false);
  const [ssoPending, setSsoPending] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  // Dashboard OAuth callback redirect về ?error=<code> — map sang tiếng Việt
  // giống trang /dang-nhap của dashboard.
  const oauthError = oauthErrorMessage(useSearchParams().get("error"));
  const form = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: SignInInput) {
    setSubmitting(true);
    try {
      // Same-origin proxy (gọi dashboard server-to-server + forward Set-Cookie) —
      // fetch thẳng dashboard.vucar.vn là cross-origin nên preflight bị CORS chặn
      // → "Lỗi kết nối" cho ai chưa có cookie .vucar.vn. Xem backend/api/workspace/[...action].
      const res = await fetch("/api/workspace/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        form.setError("password", {
          message: data.error ?? "Tài khoản hoặc mật khẩu không chính xác.",
        });
        return;
      }
      // Server gate re-evaluates on full reload and renders the app.
      window.location.href = "/";
    } catch {
      form.setError("password", { message: "Lỗi kết nối. Vui lòng thử lại." });
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogle() {
    setSsoPending(true);
    setSsoError(null);
    try {
      await signInWithGoogle();
      // Thành công thì browser navigate sang Google — giữ trạng thái pending.
    } catch (e) {
      setSsoError(e instanceof Error ? e.message : "Đăng nhập với Google thất bại.");
      setSsoPending(false);
    }
  }

  const busy = submitting || ssoPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-xl border bg-card p-8 shadow-sm">
        <h1 className="app-page-title mb-1">Đăng nhập</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Vucar E2E · Quản lý lead &amp; dealer
        </p>
        {(oauthError || ssoError) && (
          <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {oauthError ?? ssoError}
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={busy}
          onClick={onGoogle}
        >
          {ssoPending ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <GoogleLogo />
          )}
          Đăng nhập với Google
        </Button>
        <div className="my-4 flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase text-muted-foreground">hoặc</span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Tên tài khoản</FormLabel>
                  <FormControl>
                    <Input placeholder="Nhập email" autoComplete="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Mật khẩu</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Nhập mật khẩu"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={busy}>
              {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
              Đăng nhập
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}

export function LoginScreen() {
  return (
    <Suspense>
      <LoginScreenInner />
    </Suspense>
  );
}
